import React, { useState, useEffect, useCallback } from 'react';
import { Activity, ExternalLink, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { authFetch } from '../lib/authFetch';

/**
 * MarketingEngagementWidget
 * -------------------------
 * SuperAdmin dashboard card showing 30-day engagement metrics for the public
 * 60-second tour: views, completion rate, CTA clicks, top UTM sources.
 * Backed by GET /api/admin/marketing-events/summary.
 */
export default function MarketingEngagementWidget() {
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/marketing-events/summary`);
      if (r.ok) {
        const j = await r.json();
        setData(j);
      }
    } catch (_) { /* swallow */ }
    setRefreshing(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <div className="mb-4 bg-zinc-900 border border-zinc-800 rounded-2xl p-4 text-xs text-zinc-500" data-testid="marketing-engagement-widget-loading">
        Loading engagement data…
      </div>
    );
  }

  const { tour_views = 0, tour_completes = 0, tour_cta_clicks = 0, completion_rate_pct = 0, top_sources = [] } = data;

  return (
    <div className="mb-4 bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden" data-testid="marketing-engagement-widget">
      <button
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-800/50 transition-colors"
        data-testid="marketing-widget-toggle"
      >
        <div className="flex items-center gap-3">
          <Activity className="w-4 h-4 text-blue-400" />
          <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-300">Tour Engagement (30d)</span>
          <span className="text-[11px] text-zinc-500">
            <span className="font-bold text-white">{tour_views}</span> views ·
            <span className="font-bold text-white"> {completion_rate_pct}%</span> completed ·
            <span className="font-bold text-white"> {tour_cta_clicks}</span> CTA clicks
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); load(); }}
            className="p-1 rounded hover:bg-zinc-700"
            title="Refresh"
            data-testid="marketing-widget-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-zinc-400 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          {collapsed ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronUp className="w-4 h-4 text-zinc-500" />}
        </div>
      </button>

      {!collapsed && (
        <div className="px-4 pb-4 pt-1 border-t border-zinc-800" data-testid="marketing-widget-expanded">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Stat label="Tour Views" value={tour_views} />
            <Stat label="Completed" value={tour_completes} />
            <Stat label="Skipped" value={data.tour_skips || 0} />
            <Stat label="CTA Clicks" value={tour_cta_clicks} />
          </div>

          {top_sources.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-1.5">
                <ExternalLink className="w-3 h-3" /> Top Sources
              </div>
              <div className="space-y-1">
                {top_sources.map((s) => (
                  <div key={s.source} className="flex items-center justify-between text-[12px]">
                    <span className="text-zinc-300 truncate">{s.source}</span>
                    <span className="font-mono text-zinc-400">{s.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {top_sources.length === 0 && tour_views === 0 && (
            <p className="text-[12px] text-zinc-500 italic">
              No engagement data yet. Tour views will appear here once visitors land on /tour or click the &ldquo;Watch 60-sec Tour&rdquo; button on the landing page.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-zinc-800/50 rounded-lg px-3 py-2">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-1">{label}</div>
      <div className="text-lg font-bold text-white tabular-nums">{value}</div>
    </div>
  );
}
